import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  RefreshCw, Download, Gavel, AlertTriangle, ChevronDown, ChevronRight,
  ArrowUpRight, ArrowDownRight, Minus, Store, Tag, Building2, ShoppingBag,
} from 'lucide-react';

/**
 * High5 — Marka Arama Kampanyaları · Açık Artırma Analizi.
 *
 * Her `GG/CO/SEARCHBR/<MARKA>` kampanyasında karşımıza kimler çıkıyor.
 *
 * 🔴🔴 RAKİP BAZLI YÜZDELER (çakışma oranı, üst pozisyon oranı, üst sıralama
 *    payı) BU SAYFADA YOK ve API'den gelemez: `metrics.auction_insight_*`
 *    ailesi geliştirici jetonumuza kapalı (METRIC_ACCESS_DENIED). Bunlar yalnız
 *    Google Ads arayüzünden dışa aktarımla gelir.
 *
 * 🔑🔑 Onun yerine GÜN ORANI var: rakibin göründüğü gün ÷ bizim açık artırmada
 *    olduğumuz gün. Çakışma oranının VEKİLİ, aynısı değil — süreklilik ölçer,
 *    yoğunluk ölçmez. Bir gün içinde tek açık artırmada görünen rakip de, her
 *    açık artırmada olan rakip de o gün için 1 sayılır. Bu yüzden gün oranı
 *    "kim sahnede" sorusunu cevaplar, "kim ne kadar bastırıyor" sorusunu değil.
 *
 * 🔑 Gösterim payı KOVALANIR: %10 ve %90 sınır değerleridir, ölçüm değil. O
 *    hücrelerde "<" / ">" işareti var.
 */

const API_BASE = 'https://api.glowytics.com';
const BRAND_COLOR = '#0f766e';

const fmtInt = (n) => (n == null ? '—' : Math.round(Number(n)).toLocaleString('tr-TR'));
const fmtMoney = (n) => (n == null ? '—' : `${Math.round(Number(n)).toLocaleString('tr-TR')} ₺`);
const fmtPct = (n, d = 0) => (n == null ? '—' : `%${(Number(n) * 100).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d })}`);
const fmtDate = (s) => (s ? s.slice(8, 10) + '.' + s.slice(5, 7) : '—');

const RANGES = [
  { days: 7, label: '7 gün' },
  { days: 14, label: '14 gün' },
  { days: 30, label: '30 gün' },
  { days: 60, label: '60 gün' },
  { days: 90, label: '90 gün' },
];

const KINDS = {
  pazaryeri: { label: 'Pazaryeri', color: 'bg-orange-50 text-orange-700 border-orange-200', Icon: ShoppingBag },
  karsilastirma: { label: 'Karşılaştırma', color: 'bg-violet-50 text-violet-700 border-violet-200', Icon: Tag },
  marka: { label: 'Markanın kendisi', color: 'bg-blue-50 text-blue-700 border-blue-200', Icon: Building2 },
  perakende: { label: 'Perakendeci', color: 'bg-gray-50 text-gray-600 border-gray-200', Icon: Store },
};

/** Gösterim payı tonu — %60 üstü iyi, %30 altı zayıf. */
const shareTone = (v) => {
  if (v == null) return 'text-gray-300';
  if (v >= 0.6) return 'text-emerald-700';
  if (v >= 0.3) return 'text-amber-600';
  return 'text-red-600';
};

/** Kayıp tonu — tersine okunur: yüksek kayıp kötüdür. */
const lossTone = (v) => {
  if (v == null) return 'text-gray-300';
  if (v >= 0.5) return 'text-red-600';
  if (v >= 0.25) return 'text-amber-600';
  return 'text-gray-600';
};

function Kpi({ title, value, sub, tone }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4">
      <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">{title}</div>
      <div className={`mt-1 text-2xl font-bold tabular-nums ${tone || 'text-gray-900'}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-gray-500">{sub}</div>}
    </div>
  );
}

/** 0-100 arası tek çubuk + sayı. `bucketed` ise sayının önüne sınır işareti. */
function Bar({ value, tone, bucketed }) {
  if (value == null) return <span className="text-gray-300">—</span>;
  const pct = Math.max(0, Math.min(1, value)) * 100;
  const sign = !bucketed ? '' : value < 0.15 ? '<' : '>';
  return (
    <div className="flex items-center gap-2 justify-end">
      <div className="h-1.5 w-14 rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: BRAND_COLOR }} />
      </div>
      <span className={`tabular-nums font-semibold ${tone(value)}`}>{sign}{fmtPct(value)}</span>
    </div>
  );
}

function KindBadge({ kind }) {
  const k = KINDS[kind] || KINDS.perakende;
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium ${k.color}`}>
      <k.Icon className="w-3 h-3" /> {k.label}
    </span>
  );
}

/**
 * Son 7 gün ↔ önceki 7 gün. Gün sayısı üzerinden, yüzde üzerinden değil —
 * elimizde yüzde yok. 2 günden küçük farkı gürültü sayıp yatay gösteriyoruz.
 */
function Trend({ recent, prior }) {
  const d = (recent || 0) - (prior || 0);
  if (Math.abs(d) < 2) return <Minus className="w-3.5 h-3.5 text-gray-300 inline" />;
  return d > 0
    ? <span className="inline-flex items-center text-red-600 text-xs font-semibold"><ArrowUpRight className="w-3.5 h-3.5" />{d}</span>
    : <span className="inline-flex items-center text-emerald-600 text-xs font-semibold"><ArrowDownRight className="w-3.5 h-3.5" />{d}</span>;
}

export default function High5AuctionInsights() {
  const { selectedBrand } = useAuth();
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  const [kindFilter, setKindFilter] = useState('hepsi');

  const fetchData = async (d = days, refresh = false) => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch(
        `${API_BASE}/api/high5-auction-insights?days=${d}${refresh ? '&refresh=1' : ''}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': selectedBrand?.id || '' },
        },
      );
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      setData(await res.json());
    } catch (e) {
      setError(e.message || 'Veri alınamadı');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id]);

  const campaigns = data?.campaigns || [];

  // Toplam gösterim payı: gün gün ortalama DEĞİL, havuza göre ağırlıklı.
  const overallShare = useMemo(() => {
    const pool = campaigns.reduce((s, c) => s + (c.metrics?.pool || 0), 0);
    const imp = campaigns.reduce((s, c) => s + (c.metrics?.impressions || 0), 0);
    return pool ? imp / pool : null;
  }, [campaigns]);

  const across = useMemo(() => {
    const list = data?.across || [];
    return (kindFilter === 'hepsi' ? list : list.filter((a) => a.kind === kindFilter)).slice(0, 40);
  }, [data, kindFilter]);

  const brandOrder = useMemo(() => campaigns.map((c) => c.brand), [campaigns]);

  const exportCsv = () => {
    if (!campaigns.length) return;
    const head = [
      'Marka', 'Kampanya', 'Harcama', 'Gösterim', 'Gösterim payı', 'Mutlak üst',
      'Sıralama kaybı', 'Bütçe kaybı', 'Rakip domain', 'Rakip türü',
      'Göründüğü gün', 'Bizim aktif günümüz', 'Gün oranı', 'İlk görülme', 'Son görülme',
      'Son 7 gün', 'Önceki 7 gün',
    ];
    const lines = [head.join(';')];
    for (const c of campaigns) {
      for (const k of c.competitors) {
        lines.push([
          c.brand, c.campaignName,
          Math.round(c.metrics.cost), c.metrics.impressions,
          (c.metrics.is * 100).toFixed(1), (c.metrics.absTop * 100).toFixed(1),
          (c.metrics.rankLost * 100).toFixed(1), (c.metrics.budgetLost * 100).toFixed(1),
          k.domain, KINDS[k.kind]?.label || k.kind,
          k.days, c.activeDays, (k.presence * 100).toFixed(0),
          k.firstSeen, k.lastSeen, k.recentDays, k.priorDays,
        ].join(';'));
      }
    }
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `high5-acik-artirma-${data.start}_${data.end}.csv`;
    a.click();
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-5">
      {/* Başlık + dönem */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Gavel className="w-6 h-6" style={{ color: BRAND_COLOR }} />
            Açık Artırma Analizi — Marka Arama Kampanyaları
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Her marka kampanyasında karşımıza çıkan rakipler ve o kampanyadaki gösterim payımız.
            {data && <> Dönem <strong>{fmtDate(data.start)} – {fmtDate(data.end)}</strong> ({data.periodDays} gün){data.cached && ' · önbellekten'}.</>}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            {RANGES.map((r) => (
              <button
                key={r.days}
                onClick={() => { setDays(r.days); fetchData(r.days); }}
                className={`px-3 py-1.5 text-sm ${days === r.days ? 'text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                style={days === r.days ? { background: BRAND_COLOR } : undefined}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => fetchData(days, true)}
            disabled={loading}
            title="Önbelleği atlayıp Google Ads'ten yeniden çeker."
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Yenile
          </button>
          <button
            onClick={exportCsv}
            disabled={!campaigns.length}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            <Download className="w-4 h-4" /> CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {/* Sınırı sayfanın kendisi söylüyor — sessiz varsayım bırakmıyoruz. */}
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <strong>Yüzdeler Google'ın API'sinden gelmiyor.</strong> Çakışma oranı, üst pozisyon oranı ve üst
          sıralama payı geliştirici jetonumuza kapalı (METRIC_ACCESS_DENIED). Rakiplerin <em>kim</em> olduğu
          ve <em>hangi günlerde</em> sahnede oldukları geliyor — aşağıdaki <strong>gün oranı</strong> bundan
          hesaplanıyor. Gün oranı çakışma oranının vekilidir, aynısı değil: süreklilik ölçer, yoğunluk ölçmez.
          Gerçek yüzdeler için Google Ads arayüzünden dışa aktarım gerekiyor.
        </div>
      </div>

      {/* Özet */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi title="Marka kampanyası" value={fmtInt(data.totals.campaigns)} sub={`${fmtInt(data.totals.competitors)} tekil rakip domain`} />
          <Kpi
            title="Gösterim payımız"
            value={overallShare == null ? '—' : fmtPct(overallShare, 1)}
            sub={`${fmtInt(data.totals.impressions)} gösterim · havuz ${fmtInt(data.totals.pool)}`}
            tone={shareTone(overallShare)}
          />
          <Kpi title="Dönem harcaması" value={fmtMoney(data.totals.cost)} sub={`${fmtInt(data.totals.clicks)} tıklama`} />
          <Kpi
            title="Kaçırdığımız gösterim"
            value={fmtInt(Math.max(0, data.totals.pool - data.totals.impressions))}
            sub="Açık artırmaya girdiğimiz halde görünmediğimiz gösterim"
            tone="text-red-600"
          />
        </div>
      )}

      {/* Kampanya tablosu */}
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Marka kampanyaları</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Satıra tıkla, o kampanyanın rakip rafı açılsın. Harcamaya göre sıralı.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Marka</th>
                <th className="px-3 py-2 text-right font-semibold">Harcama</th>
                <th className="px-3 py-2 text-right font-semibold">Gösterim</th>
                <th className="px-3 py-2 text-right font-semibold">Gösterim payı</th>
                <th className="px-3 py-2 text-right font-semibold">Mutlak üst</th>
                <th className="px-3 py-2 text-right font-semibold">Sıralama kaybı</th>
                <th className="px-3 py-2 text-right font-semibold">Bütçe kaybı</th>
                <th className="px-3 py-2 text-right font-semibold">Rakip</th>
                <th className="px-3 py-2 text-right font-semibold">Sürekli</th>
                <th className="px-3 py-2 text-center font-semibold">Hareket</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {campaigns.map((c) => {
                const m = c.metrics;
                const isOpen = !!open[c.campaignId];
                return (
                  <React.Fragment key={c.campaignId}>
                    <tr
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setOpen((o) => ({ ...o, [c.campaignId]: !o[c.campaignId] }))}
                    >
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                          <span className="font-semibold text-gray-900">{c.brand}</span>
                          {c.status && c.status !== 'ENABLED' && (
                            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">{c.status}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmtMoney(m.cost)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmtInt(m.impressions)}</td>
                      <td className="px-3 py-2 text-right"><Bar value={m.is} tone={shareTone} bucketed={c.bucketed} /></td>
                      <td className={`px-3 py-2 text-right tabular-nums ${shareTone(m.absTop)}`}>{fmtPct(m.absTop)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${lossTone(m.rankLost)}`}>{fmtPct(m.rankLost)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${lossTone(m.budgetLost)}`}>{fmtPct(m.budgetLost)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900">{c.competitorCount}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600" title="Dönemin en az %80'inde sahnede olan rakipler">{c.persistentCount}</td>
                      <td className="px-3 py-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {c.newCount > 0 && <span className="rounded bg-red-50 border border-red-200 px-1.5 py-0.5 text-[10px] text-red-700" title="Son 7 günde ilk kez göründü">+{c.newCount} yeni</span>}
                          {c.goneCount > 0 && <span className="rounded bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 text-[10px] text-emerald-700" title="Son 7 günde hiç görünmedi">−{c.goneCount} çekildi</span>}
                          {c.newCount === 0 && c.goneCount === 0 && <span className="text-gray-300">—</span>}
                        </div>
                      </td>
                    </tr>

                    {isOpen && (
                      <tr>
                        <td colSpan={10} className="bg-gray-50 px-3 py-3">
                          <div className="text-xs text-gray-500 mb-2">
                            {c.campaignName} · bu kampanyada {c.activeDays} gün açık artırmadaydık ·
                            {' '}{c.mix.perakende} perakendeci, {c.mix.pazaryeri} pazaryeri,
                            {' '}{c.mix.marka} markanın kendisi, {c.mix.karsilastirma} karşılaştırma sitesi
                          </div>
                          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
                            <table className="w-full text-sm">
                              <thead className="bg-white text-gray-400 text-[11px] uppercase tracking-wide border-b border-gray-100">
                                <tr>
                                  <th className="px-3 py-2 text-left font-semibold">Rakip domain</th>
                                  <th className="px-3 py-2 text-left font-semibold">Tür</th>
                                  <th className="px-3 py-2 text-right font-semibold">Gün</th>
                                  <th className="px-3 py-2 text-right font-semibold">Gün oranı</th>
                                  <th className="px-3 py-2 text-center font-semibold">İlk</th>
                                  <th className="px-3 py-2 text-center font-semibold">Son</th>
                                  <th className="px-3 py-2 text-center font-semibold">Son 7g ↔ önceki 7g</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-50">
                                {c.competitors.map((k) => (
                                  <tr key={k.domain} className={k.isGone ? 'opacity-50' : ''}>
                                    <td className="px-3 py-1.5 font-medium text-gray-800">
                                      {k.domain}
                                      {k.isNew && <span className="ml-2 rounded bg-red-50 border border-red-200 px-1.5 py-0.5 text-[10px] text-red-700">yeni</span>}
                                      {k.isGone && <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">çekildi</span>}
                                    </td>
                                    <td className="px-3 py-1.5"><KindBadge kind={k.kind} /></td>
                                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">{k.days}/{c.activeDays}</td>
                                    <td className="px-3 py-1.5 text-right">
                                      <div className="flex items-center gap-2 justify-end">
                                        <div className="h-1.5 w-16 rounded-full bg-gray-100 overflow-hidden">
                                          <div className="h-full rounded-full" style={{ width: `${Math.min(100, k.presence * 100)}%`, background: BRAND_COLOR }} />
                                        </div>
                                        <span className="tabular-nums font-semibold text-gray-700 w-10 text-right">{fmtPct(k.presence)}</span>
                                      </div>
                                    </td>
                                    <td className="px-3 py-1.5 text-center tabular-nums text-gray-500 text-xs">{fmtDate(k.firstSeen)}</td>
                                    <td className="px-3 py-1.5 text-center tabular-nums text-gray-500 text-xs">{fmtDate(k.lastSeen)}</td>
                                    <td className="px-3 py-1.5 text-center tabular-nums text-gray-600 text-xs">
                                      {k.recentDays} ↔ {k.priorDays} <span className="ml-1"><Trend recent={k.recentDays} prior={k.priorDays} /></span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {!campaigns.length && !loading && (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-gray-400">Veri yok</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Rakip haritası: kim tek markada, kim her rafımızda */}
      {!!(data?.across || []).length && (
        <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-end justify-between gap-2">
            <div>
              <h2 className="font-semibold text-gray-900">Rakip haritası</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Hangi rakip kaç marka kampanyamızda karşımıza çıkıyor. Tek markada görünen bir rakiple
                portföyün tamamında görünen bir rakip aynı sorun değil. Hücredeki sayı gün oranı.
              </p>
            </div>
            <div className="flex rounded-lg border border-gray-300 overflow-hidden text-xs">
              {['hepsi', 'perakende', 'pazaryeri', 'marka', 'karsilastirma'].map((k) => (
                <button
                  key={k}
                  onClick={() => setKindFilter(k)}
                  className={`px-2.5 py-1.5 ${kindFilter === k ? 'text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                  style={kindFilter === k ? { background: BRAND_COLOR } : undefined}
                >
                  {k === 'hepsi' ? 'Hepsi' : KINDS[k].label}
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-[11px] uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold sticky left-0 bg-gray-50">Rakip domain</th>
                  <th className="px-3 py-2 text-right font-semibold">Kampanya</th>
                  {brandOrder.map((b) => (
                    <th key={b} className="px-1.5 py-2 text-center font-semibold whitespace-nowrap">{b}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {across.map((a) => {
                  const byBrand = Object.fromEntries(a.campaigns.map((x) => [x.brand, x]));
                  return (
                    <tr key={a.domain} className="hover:bg-gray-50">
                      <td className="px-3 py-1.5 sticky left-0 bg-white">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-800">{a.domain}</span>
                          <KindBadge kind={a.kind} />
                        </div>
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-gray-900">{a.campaignCount}</td>
                      {brandOrder.map((b) => {
                        const hit = byBrand[b];
                        if (!hit) return <td key={b} className="px-1.5 py-1.5 text-center text-gray-200">·</td>;
                        // Tek renk üzerinde doygunluk: seyrek görünen açık, sürekli olan koyu.
                        const bg = `rgba(15, 118, 110, ${(0.08 + Math.min(1, hit.presence) * 0.6).toFixed(3)})`;
                        return (
                          <td
                            key={b}
                            className={`px-1.5 py-1.5 text-center tabular-nums text-xs font-semibold ${hit.presence >= 0.6 ? 'text-white' : 'text-gray-800'}`}
                            style={{ background: bg }}
                            title={`${a.domain} · ${b} kampanyasında ${hit.days} gün`}
                          >
                            {Math.round(hit.presence * 100)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {(data.across || []).length > across.length && (
            <div className="px-4 py-2 text-xs text-gray-400 border-t border-gray-100">
              İlk {across.length} rakip gösteriliyor ({data.across.length} tekil domain var). Tamamı CSV'de.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
